import { WarningAmberTwoTone } from '@mui/icons-material';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle
} from '@mui/material';
import { useFeatureEnabled } from 'api/appConfig';
import {
	useConfigs,
	useJITDisabled,
	useUpdateConfigs
} from 'api/configurations';
import { useRevokeScimKey, useRotateAndRevokeScimKey } from 'api/scimKeys';
import LoadingButton from 'components/shared/LoadingButton';
import React, { useCallback, useState } from 'react';
import { IScimRevokeActionsDialogResult } from 'types/dialogs';
import { S4Feature } from 'types/api';
import { updateJITDisabled } from 'utils/saveConfigs';

type ScimRevokeActionType = 'revoke' | 'rotateAndRevoke';

interface IScimRevokeActionsDialogProps {
	actionType: ScimRevokeActionType;
	onClose: (res: IScimRevokeActionsDialogResult) => void;
}

const ScimRevokeActionsDialog: React.FC<IScimRevokeActionsDialogProps> = ({
	actionType,
	onClose
}) => {
	const [showJitDisabled, setShowJitDisabled] = useState(false);

	const { enabled: jitGuideEnabled } = useFeatureEnabled(S4Feature.JitGuide);
	const revokeScimKey = useRevokeScimKey();
	const rotateAndRevokeScimKey = useRotateAndRevokeScimKey();
	const { data: jitDisabled } = useJITDisabled();
	const { data: configsData } = useConfigs();
	const updateConfigsMutation = useUpdateConfigs(
		intl.tr('scimConfig.saveJit.saveSuccessMessage'),
		intl.tr('scimConfig.saveJit.saveErrorMessage')
	);

	const handleCancel = useCallback(() => {
		onClose({ success: false });
	}, [onClose]);

	const handleConfirmScimAction = useCallback(async () => {
		try {
			if (actionType === 'revoke') {
				await revokeScimKey();
				if (jitDisabled && jitGuideEnabled) {
					setShowJitDisabled(true);
					return;
				}
				onClose({ success: true });
				return;
			}

			const res = await rotateAndRevokeScimKey();
			onClose({ success: true, cleartext: res?.cleartext });
		} catch {
			onClose({ success: false });
		}
	}, [
		actionType,
		rotateAndRevokeScimKey,
		onClose,
		revokeScimKey,
		jitDisabled,
		jitGuideEnabled
	]);

	const handleEnableJit = useCallback(async () => {
		if (!configsData) return;
		try {
			const updatedConfig = updateJITDisabled(false, configsData);
			await updateConfigsMutation.mutateAsync(updatedConfig);
			onClose({ success: true });
		} catch {
			// JIT  update has failed but key revoked successfully.
			onClose({ success: true });
		}
	}, [configsData, updateConfigsMutation, onClose]);

	const handleSkipEnableJit = useCallback(() => {
		onClose({ success: true });
	}, [onClose]);

	let title = '';
	let message = '';
	let confirmButton = '';

	if (actionType === 'revoke') {
		title = intl.tr('scimConfig.dialog.revoke.title');
		message = intl.tr('scimConfig.dialog.revoke.message');
		confirmButton = intl.tr('scimConfig.dialog.revoke.confirm');
	} else if (actionType === 'rotateAndRevoke') {
		title = intl.tr('scimConfig.dialog.rotateAndRevoke.title');
		message = intl.tr('scimConfig.dialog.rotateAndRevoke.message');
		confirmButton = intl.tr('scimConfig.dialog.rotateAndRevoke.confirm');
	}

	if (showJitDisabled) {
		return (
			<Dialog open maxWidth="sm" fullWidth PaperProps={{ elevation: 0 }}>
				<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<WarningAmberTwoTone />
					{intl.tr('scimConfig.dialog.jitDisabled.title')}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{intl.tr('scimConfig.dialog.jitDisabled.message')}
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button variant="outlined" onClick={handleSkipEnableJit}>
						{intl.tr('general.cancel')}
					</Button>
					<LoadingButton
						variant="contained"
						onClick={handleEnableJit}
						color="primary"
					>
						{intl.tr('scimConfig.dialog.jitDisabled.enable')}
					</LoadingButton>
				</DialogActions>
			</Dialog>
		);
	}

	return (
		<Dialog open maxWidth="sm" fullWidth PaperProps={{ elevation: 0 }}>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				<DialogContentText>{message}</DialogContentText>
			</DialogContent>
			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button variant="outlined" onClick={handleCancel}>
					{intl.tr('general.cancel')}
				</Button>
				<LoadingButton
					variant="contained"
					onClick={handleConfirmScimAction}
					color="error"
				>
					{confirmButton}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default ScimRevokeActionsDialog;
export type { ScimRevokeActionType as ScimConfirmActionType };
